import { useEffect, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../../../DemoState/const";
import loader from "../../../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../../../extensions/date";

interface SummaryData {
  date: string;
  month: string;
  oar: number;
  gna_cost: number;
  gna_quantum: number;
  tgna_cost: number;
  tgna_quantum: number;
  optimal_gna: number;
  total_cost: number;
  slot: number;
}

export default function DailyVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  selectedTargetDate,
  data,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  selectedTargetDate: any[];
  data: any[];
}) {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);

  // --- OLD CHART SUFFIXES (Keep for safety/fallback if needed) ---
  const chartSuffixes = [
    {
      suffix: "tgna_quantum",
      displayName: "Optimal TGNA Utilisation",
      stackSuffix: "tgna_quantum",
      stroke: "rgba(255, 194, 158, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    // ... others ...
  ];

  const [selectedMetrics] = useState<any[]>(chartSuffixes);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);

  useEffect(() => {



    if (!data || !Array.isArray(data) || data.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    // Check if this is the new scenario-based data format
    // Assuming data[0].years exists based on previous patterns and user request
    // Check if this is the new scenario-based data format
    // Handle both 'years' (Capacity Daily) and 'dates' (Capacity Monthly or potential Cost API variance)
    const firstItem = data[0];
    const hasDataYearwise = firstItem && 'data_yearwise' in firstItem && Array.isArray(firstItem.data_yearwise);
    const hasYears = firstItem && 'years' in firstItem && Array.isArray(firstItem.years);
    const hasDates = firstItem && 'dates' in firstItem && Array.isArray(firstItem.dates);

    if (hasDataYearwise || hasYears || hasDates) {

      const transformedData: any[] = [];
      const allYears = new Set<string>();

      // Collect all years
      data.forEach((scenario: any) => {
        const timeArray = scenario.data_yearwise || scenario.years || scenario.dates || [];
        timeArray.forEach((item: any) => {
          // Handle if 'year' is a property or if we need to extract from 'date' string
          if (item.year) allYears.add(item.year.toString());
          else if (item.date) allYears.add(item.date.split('-')[0]); // Fallback
        });
      });

      // Create chart row for each year
      Array.from(allYears).sort().forEach((year) => {
        const row: any = { year: year.toString() };

        data.forEach((scenario: any, index: number) => {
          // Use scenario_number if present, else fallback to index + 1
          const sNum = scenario.scenario_number !== undefined ? scenario.scenario_number : (index + 1);
          const scenarioLabel = `Scenario_${sNum}`;

          const timeArray = scenario.data_yearwise || scenario.years || scenario.dates || [];
          const yearData = timeArray.find((y: any) => {
            if (y.year) return y.year.toString() === year;
            if (y.date) return y.date.startsWith(year);
            return false;
          });

          if (yearData) {
            const baseKey = `${scenarioLabel}_base_cost`;
            const addedKey = `${scenarioLabel}_added_scenario_cost`;

            row[baseKey] = Number(yearData.base_cost || 0);
            row[addedKey] = Number(yearData.added_scenario_cost || 0);
          }
        });

        transformedData.push(row);
      });

      // Extract keys from ALL rows (union of keys)
      const allKeys = new Set<string>();
      transformedData.forEach(row => {
        Object.keys(row).forEach(key => {
          if (key !== 'year') allKeys.add(key);
        });
      });
      const keys = Array.from(allKeys);

      setChartData(transformedData);
      setChartKeys(keys);
      return;
    } else if (data[0] && ('base_cost' in data[0] || 'added_scenario_cost' in data[0])) {
      // FLAT STRUCTURE HANDLING

      const transformedData: any[] = [];
      const allYears = new Set<string>();

      data.forEach((item: any) => {
        if (item.year) allYears.add(item.year.toString());
        else if (item.date) allYears.add(item.date.split('-')[0]);
      });

      Array.from(allYears).sort().forEach((year) => {
        const row: any = { year: year.toString() };
        // Find all items for this year
        const yearItems = data.filter((d: any) => {
          return (d.year && d.year.toString() === year) || (d.date && d.date.startsWith(year));
        });

        yearItems.forEach((item: any) => {
          const sNum = item.scenario_number !== undefined ? item.scenario_number : item.scenario_id;
          // If no scenario number/id, we might have trouble grouping. 
          // Assuming scenario_number is present in flat data.
          if (sNum !== undefined) {
            const scenarioLabel = `Scenario_${sNum}`;
            const baseKey = `${scenarioLabel}_base_cost`;
            const addedKey = `${scenarioLabel}_added_scenario_cost`;

            row[baseKey] = Number(item.base_cost || 0);
            row[addedKey] = Number(item.added_scenario_cost || 0);
          }
        });

        transformedData.push(row);
      });

      const allKeys = new Set<string>();
      transformedData.forEach(row => {
        Object.keys(row).forEach(key => { if (key !== 'year') allKeys.add(key); });
      });
      const keys = Array.from(allKeys);

      setChartData(transformedData);
      setChartKeys(keys);
      return;
    }

    // Fallback or Old Logic
    // ... (Existing logic can remain or be replaced if we are sure about replacement)
    // For now, I'll clear it if it doesn't match to avoid errors, or keep existing for safety.
    setChartData([]);
    setChartKeys([]);

  }, [data, selectedBlock, selectedTargetDate]);

  const createChartObject = (
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    fill: string,
    yAxisId: string
  ) => {
    return {
      dataKey: suffix,
      name: displayName,
      legendColor: fill === "none" ? stroke : fill,
      type: chartType,
      unit: unit,
      stroke: stroke,
      fill: fill,
      yAxisId: yAxisId,
      stackId: stackSuffix || undefined,
    };
  };

  function getLegends() {
    if (chartKeys.length > 0 && chartKeys[0].includes('Scenario')) {
      // Keys: Scenario_1_base_cost, Scenario_1_added_scenario_cost

      return chartKeys.map((key: string) => {
        const parts = key.split('_'); // ["Scenario", "1", "base", "cost"] or ["Scenario", "1", "added", "scenario", "cost"]
        const scenarioNum = parts[1];
        const metricOriginal = parts.slice(2).join('_'); // "base_cost" or "added_scenario_cost"

        let displayNameSuffix = "";
        let color = "#000";

        if (metricOriginal === 'base_cost') {
          displayNameSuffix = "Base Cost";
          color = "#94C0FF"; // Deep Blue
        } else if (metricOriginal === 'added_scenario_cost') {
          displayNameSuffix = "Added Scenario Cost";
          color = "#F4C389"; // Orange
        }

        const displayName = `Scenario ${scenarioNum} ${displayNameSuffix}`;
        const stackSuffix = `${parts[0]}_${scenarioNum}`; // Stack by scenario? Or separate?
        // If we want side-by-side per scenario, usually we might not stack them or stack them in a specific group.
        // Let's stack them together for the scenario to show total? Or side by side?
        // "display them year wise scenario wise" usually implies comparison.
        // Let's NOT stack base and added cost on top of each other unless they are additive parts of a total. 
        // "added_scenario_cost" sounds additive. 
        // Let's stack them: Scenario_1 group.

        return createChartObject(
          key,
          displayName,
          stackSuffix, // Stack items for the same scenario together?
          ChartType.Bar,
          "INR",
          color,
          color,
          "left"
        );
      });
    }
    return [];
  }

  return (
    <>
      {loading ? (
        <div className="w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center h-[400px]">
          <p className="text-gray-500 !text-sm">Please Select Scenarios to View Data</p>
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 50px)"
          data={chartData}
          brushStart={BrushStart.Start}
          isTimeSlot={false}
          onlyTitle={true}
          legends={getLegends()}
          yAxisLabel={"Cost (INR)"}
          nameGap={40}
          xDataKey={"year"}
          top={10}
          bottom={30}
          showToolbox={false}
        />
      )}
    </>
  );
}
